import { ROW_HEIGHT } from "../../../../constants/constants";
import { camelCase, isEqual } from "lodash";
import {
  useDataSourceGroups,
  useHighlightedTags,
  useIsTimelineExpanded,
} from "components/Dashboard/HubletView/SingleTimeline/singleTimelineStore";
import { TAGHIGHLIGHT_BACKDROP_HEIGHT } from "components/Dashboard/HubletView/SingleTimeline/TagHighlightTimelineBackdrop";
import { Fade } from "@mui/material";
import { darkGrey } from "constants/colors";
import { ProductionSegmentStyles } from "./ProductionSegmentStyles";
import { useSelector } from "store/useSelector";
import { ProductionSegmentType } from "types/types";
import {
  useGetPageFromPath,
  useTimeAxisContext,
  useVisibleProcesses,
} from "utils/customHooks/genericHooks";
import * as d3 from "d3";
import { useTimelineTooltipStore } from "../TimelineTooltip/timelineTooltipStore";
import { useMemo } from "react";

export function ProductionSubSegments({
  process: processProp,
}: {
  process?: { id: string; name: string };
}) {
  const segments = useSelector(state => state.legacy.segments);

  const visibleProcesses = useVisibleProcesses({
    getSingleProcessOnly: true,
  });
  const process = processProp ?? visibleProcesses[0];
  const [highlightedTags] = useHighlightedTags();
  const { selectedDataSourceGroups } = useDataSourceGroups();
  const segmentsFiltered = useMemo(() => {
    if (!process) {
      return [];
    }
    const processSegments = segments[process.id];
    const highlightedTagNames = highlightedTags.map(h => h.name);
    const selectedDataSourceGroupNames = selectedDataSourceGroups.map(
      h => h.name
    );
    const shouldFilter =
      highlightedTagNames.length > 0 || selectedDataSourceGroupNames.length > 0;
    const segmentsFiltered = shouldFilter
      ? processSegments.filter(
          segment =>
            (segment.tag?.name &&
              highlightedTagNames.includes(segment.tag.name)) ||
            segment.events?.some(
              event =>
                event.datasourceGroup?.name &&
                (selectedDataSourceGroupNames.includes(
                  event.datasourceGroup.name
                ) ||
                  highlightedTagNames.includes(event.datasourceGroup?.name))
            )
        )
      : processSegments;
    return segmentsFiltered;
  }, [segments, highlightedTags, selectedDataSourceGroups, process]);

  return (
    <>
      {segmentsFiltered.map((segment, i) => (
        <ProductionSubSegment key={i} segment={segment} process={process} />
      ))}
    </>
  );
}

function ProductionSubSegment({
  process,
  segment,
}: {
  segment: ProductionSegmentType;
  process: { id: string; name: string };
}) {
  const now = Date.now();
  const [, setTimelineTooltipStore] = useTimelineTooltipStore();
  const { xLeft, xRight, scaleTime } = useTimeAxisContext();
  const isCurrentSegment = !segment.range.lt;
  const { isTimelinePage, isHubletPage } = useGetPageFromPath();

  // Nulls on the range mean the segment extends forever in either direction
  const x0 = segment.range.ge ? scaleTime(segment.range.ge) || 0 : xLeft;
  const x1 =
    scaleTime(isCurrentSegment ? now : (segment.range.lt as number)) || 0;
  const [isTimelineExpanded] = useIsTimelineExpanded();

  const pathLeft = Math.max(x0, xLeft);
  const pathTop = TAGHIGHLIGHT_BACKDROP_HEIGHT / 2;
  const pathRight = Math.min(x1, xRight);

  const areaPath = d3.line()([
    [pathLeft, pathTop],
    [pathRight, pathTop],
  ]);

  if (!areaPath) {
    return null;
  }

  // Check if no part of the segment will be displayed
  const isSegmentOutsideOfFocusRange = x0 >= x1 || x0 >= xRight || x1 < xLeft;
  if (isSegmentOutsideOfFocusRange) {
    return null;
  }

  const rectRight = Math.min(x1, xRight);
  const rectLeft = Math.max(x0, xLeft);
  const rectWidth = rectRight - rectLeft;

  if (rectWidth <= 0 || isNaN(rectLeft) || !segment) {
    return null;
  }
  let translateY = 0;
  if (isTimelinePage) {
    translateY = 4.5;
  } else if (isHubletPage) {
    translateY = 7.5 + (isTimelineExpanded ? 14.5 : 0);
  }
  return (
    <ProductionSegmentStyles
      {...{
        $translateY: translateY,
        $categoriesToShrink: [],
      }}
      onMouseOver={() => {
        const modalTime =
          segment.range?.ge && segment.range?.lt
            ? Number(segment.range.ge) +
              (Number(segment.range.lt) - Number(segment.range.ge)) / 2
            : 0;
        setTimelineTooltipStore(p => {
          return {
            ...p,
            machemetryId: process.id,
            selectedTimelineIdx: 0,
            modal: {
              t: modalTime,
              clickIndex: 0,
            },
            selectedProdSegment:
              // prevents re-rendering TimelineTooltipTagDisplay when mousing over the same segment
              isEqual(segment, p.selectedProdSegment)
                ? p.selectedProdSegment
                : segment,
          };
        });
      }}
    >
      <HighlightedTagSegment
        {...{
          rectLeft,
          rectWidth,
          segment,
          now,
          segmentDataset: (segment as any).dataset,
        }}
      />
      <HighlightedDataSourceSegment
        {...{
          rectLeft,
          rectWidth,
          segment,
          now,
          segmentDataset: (segment as any).dataset,
        }}
      />
    </ProductionSegmentStyles>
  );
}

export function HighlightedTagSegment({
  rectLeft,
  rectWidth,
  segment,
  segmentDataset,
}) {
  const [highlightedTags] = useHighlightedTags();

  const allSegmentMetadata = [
    segment.tag?.name,
    ...(segment.meta?.explanations ?? []),
    ...(segment.meta?.traceability ?? []),
  ].map(camelCase);

  const associatedHighlightedTag = segment.tag
    ? highlightedTags.find(tag =>
        allSegmentMetadata.includes(camelCase(tag.name))
      )
    : null;

  return (
    <Fade
      in={Boolean(associatedHighlightedTag)}
      mountOnEnter={true}
      unmountOnExit={true}
    >
      <rect
        x={rectLeft}
        y={ROW_HEIGHT - 0.5}
        height={TAGHIGHLIGHT_BACKDROP_HEIGHT}
        rx={2}
        ry={2}
        width={rectWidth}
        className={`tag-highlight tag-highlight-${camelCase(
          associatedHighlightedTag?.name
        )}`}
        fill={
          associatedHighlightedTag?.highlightColor ||
          darkGrey /* show darkGrey as it fades out */
        }
        {...segmentDataset}
      />
    </Fade>
  );
}

export function HighlightedDataSourceSegment({
  rectLeft,
  rectWidth,
  segment,
  segmentDataset,
}) {
  const [highlightedTags] = useHighlightedTags();

  const segmentDataSources = segment.events
    ?.map(event => event.datasourceGroup?.name)
    .filter(Boolean); // filter out any falsies/empties

  const index = segmentDataSources
    ? highlightedTags.findIndex(tag => segmentDataSources.includes(tag.name))
    : -1;
  const associatedHighlightedDataSource =
    segmentDataSources && index !== -1 && highlightedTags[index];

  return (
    <Fade
      in={Boolean(associatedHighlightedDataSource)}
      mountOnEnter={true}
      unmountOnExit={true}
    >
      <rect
        x={rectLeft}
        y={ROW_HEIGHT + 6 + index * 6} // since this highlight can overlap the one above, make it shorter & lower down
        height={6}
        width={rectWidth}
        className={`tag-highlight tag-highlight-datasourcegroup tag-highlight-${camelCase(
          associatedHighlightedDataSource?.name
        )}`}
        fill={associatedHighlightedDataSource?.highlightColor || darkGrey}
        {...segmentDataset}
      />
    </Fade>
  );
}
